import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HomeSearchForm from '../components/HomeSearchForm';
import SEO from '../components/SEO';
import AirlineCallPopup from '../components/AirlineCallPopup';
import Logo from '../components/Logo';
import { useContact } from '../context/ContactContext';

const jsonLd = {
  '@context':'https://schema.org','@type':'WebSite',name:'Flyairlinebooking',url:'https://flyairlinebooking.com',
  description:'Flyairlinebooking is an independent travel agency helping travelers find and book domestic and international flights with transparent pricing and 24/7 support.',
  potentialAction:{'@type':'SearchAction',target:'https://flyairlinebooking.com/flights?origin={origin}&destination={destination}&date={date}&adults=1','query-input':'required name=origin required name=destination required name=date'},
};

const svg = (d) => (
  <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const ICONS = {
  tag: svg('M7 7h.01M3 11.5V4a1 1 0 0 1 1-1h7.5a1 1 0 0 1 .7.3l8.5 8.5a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 12.2a1 1 0 0 1-.3-.7z'),
  headset: svg('M4 14v-2a8 8 0 1 1 16 0v2M4 14a2 2 0 0 0 2 2h1v-5H6a2 2 0 0 0-2 2zm16 0a2 2 0 0 1-2 2h-1v-5h1a2 2 0 0 1 2 2zm-3 2v1a3 3 0 0 1-3 3h-2'),
  globe: svg('M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zm0 0c2.5-2.4 3.8-5.4 3.8-9S14.5 5.4 12 3m0 18c-2.5-2.4-3.8-5.4-3.8-9S9.5 5.4 12 3M3.5 9h17M3.5 15h17'),
  chat: svg('M8 10h8M8 14h5m-7 6 2.5-3H18a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3H6a3 3 0 0 0-3 3v7a3 3 0 0 0 3 3'),
  lock: svg('M7 11V8a5 5 0 0 1 10 0v3M6 11h12a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1z'),
  check: svg('M5 12.5l4.5 4.5L19 7.5'),
};

const WHY_CHOOSE = [
  { icon:'tag', title:'Transparent pricing', desc:'Pricing and service-related information is disclosed throughout the booking process, so you understand applicable charges and travel terms before you pay.' },
  { icon:'headset', title:'Professional travel assistance', desc:'Our experienced support team helps with booking inquiries, itinerary reviews, travel information and general flight questions.' },
  { icon:'globe', title:'Domestic & international', desc:'Whether you are flying within the United States or planning an international trip, we assist with a wide range of travel requirements.' },
  { icon:'chat', title:'Customer-focused service', desc:'We help travelers navigate airline policies, schedule changes and travel concerns with responsive support whenever possible.' },
  { icon:'lock', title:'Secure booking', desc:'We use industry-standard security measures to help safeguard the personal information you submit through our website.' },
];

const ROUTES = [
  { from:'JFK', to:'LAX', fromCity:'New York',      toCity:'Los Angeles', tag:'Coast to coast' },
  { from:'ORD', to:'MIA', fromCity:'Chicago',       toCity:'Miami',       tag:'Beach escape' },
  { from:'DFW', to:'LAS', fromCity:'Dallas',        toCity:'Las Vegas',   tag:'Weekend trip' },
  { from:'SFO', to:'SEA', fromCity:'San Francisco', toCity:'Seattle',     tag:'West coast' },
  { from:'BOS', to:'MCO', fromCity:'Boston',        toCity:'Orlando',     tag:'Family favorite' },
  { from:'ATL', to:'JFK', fromCity:'Atlanta',       toCity:'New York',    tag:'City break' },
  { from:'LAX', to:'HNL', fromCity:'Los Angeles',   toCity:'Honolulu',    tag:'Island getaway' },
  { from:'IAH', to:'DEN', fromCity:'Houston',       toCity:'Denver',      tag:'Mountain trip' },
];

const STEPS = [
  { n:'1', title:'Search', desc:'Enter your route, dates and travelers to see available flight options.' },
  { n:'2', title:'Compare & choose', desc:'Review schedules, fares and fare rules, and pick the option that fits your trip.' },
  { n:'3', title:'Book with support', desc:'Complete your booking online or call our team for help at any step.' },
];

const SUPPORT_ITEMS = [
  'Flight booking assistance',
  'Travel itinerary questions',
  'General airline policy guidance',
  'Schedule change inquiries',
  'Cancellation information',
  'Refund-related guidance',
  'Travel documentation questions',
];

const FAQS = [
  { q:'Is Flyairlinebooking an airline?', a:'No. Flyairlinebooking is an independent travel agency and is not an airline.' },
  { q:'Are you affiliated with any airline?', a:'No. We are not affiliated with, endorsed by, or associated with any airline.' },
  { q:'Can flight prices change?', a:'Yes. Airline fares are subject to change until booking confirmation and ticket issuance.' },
  { q:'Are refunds guaranteed?', a:'Refund eligibility depends on airline policies, fare conditions, and applicable travel provider rules.' },
  { q:'Can I modify my reservation?', a:'Modification options depend on airline rules and fare restrictions associated with your booking.' },
  { q:'Do you charge service fees?', a:'Applicable service fees, if any, are disclosed during the booking process and are further detailed on our Service Fees page.' },
  { q:'How can I contact customer support?', a:'You can reach our customer support team through the contact methods listed on our Contact Us page.' },
  { q:'Do you provide international travel assistance?', a:'Yes. We assist customers with both domestic and international travel inquiries.' },
  { q:'What documents are required for travel?', a:'Required travel documents vary by destination, citizenship status, and airline requirements.' },
  { q:'Where can I review your policies?', a:'You can review our Privacy Policy, Terms & Conditions, Refund Policy, and Service Fees pages directly through our website.' },
];

function SectionHeading({ eyebrow, title, sub, align = 'left' }) {
  return (
    <div className={`mb-8 sm:mb-10 ${align === 'center' ? 'text-center mx-auto' : ''} max-w-2xl`}>
      <p className="text-sm font-semibold text-blue-600 mb-2">{eyebrow}</p>
      <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">{title}</h2>
      {sub && <p className="mt-3 text-slate-600 text-base leading-relaxed">{sub}</p>}
    </div>
  );
}

export default function HomePage() {
  const [openFaq, setOpenFaq] = useState(0);
  const navigate = useNavigate();
  const { contactSettings } = useContact();
  const tfn = contactSettings?.tfn || '+1-855-895-8503';
  const tfnHref = `tel:${tfn.replace(/[^\d+]/g, '')}`;
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="bg-white">
      <AirlineCallPopup
        name="Airline Customer Service"
        code="home"
        logoNode={<Logo size="large" />}
        message="Speak with a live travel agent now for help with bookings, changes, or cancellations — 24/7."
      />

      <SEO
        title="Domestic & International Flight Booking Services"
        canonical="/"
        description="Flyairlinebooking is an independent travel agency providing flight booking assistance, travel support, and transparent pricing for domestic and international travel."
        keywords="flight booking assistance, travel support USA, domestic flights, international flights, independent travel agency, flight reservations, airline booking help"
        jsonLd={jsonLd}
      />

      {/* ── Hero + search ───────────────────────────────── */}
      <section className="relative">
        <div className="absolute inset-0 h-[560px] sm:h-[520px] lg:h-[460px] bg-gradient-to-br from-navy-950 via-navy-900 to-blue-800 overflow-hidden">
          <svg className="absolute inset-0 w-full h-full opacity-[0.12]" preserveAspectRatio="xMidYMid slice" viewBox="0 0 1200 460" fill="none" aria-hidden="true">
            <path d="M-50 380 C 250 120, 650 80, 1250 220" stroke="white" strokeWidth="1.5" strokeDasharray="6 10" />
            <path d="M-50 300 C 350 40, 800 60, 1250 120" stroke="white" strokeWidth="1" strokeDasharray="4 12" />
            <circle cx="980" cy="160" r="4" fill="white" /><circle cx="240" cy="250" r="4" fill="white" />
          </svg>
          <div className="absolute -right-24 -top-24 w-[420px] h-[420px] rounded-full bg-blue-500/20 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 lg:pt-20 pb-6">
          <div className="max-w-3xl text-white mb-8 lg:mb-10">
            <p className="inline-flex items-center gap-2 text-xs sm:text-sm font-medium text-blue-100 bg-white/10 ring-1 ring-white/15 rounded-full px-3 py-1 mb-5">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> Live agents available 24/7
            </p>
            <h1 className="font-display text-3xl sm:text-5xl lg:text-[3.4rem] font-extrabold leading-[1.1] tracking-tight">
              Book flights with real people behind every trip
            </h1>
            <p className="mt-4 text-base sm:text-lg text-blue-100/90 leading-relaxed max-w-2xl">
              Search domestic and international flights, compare options, and get help from our travel team whenever you need it.
            </p>
          </div>

          <div className="relative z-10 bg-white rounded-3xl shadow-[0_20px_60px_-15px_rgba(9,28,48,0.35)] ring-1 ring-slate-900/5 p-4 sm:p-6">
            <HomeSearchForm />
          </div>

          <ul className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-slate-700">
            {['Transparent pricing', '24/7 live support', 'Domestic & international', 'Secure checkout'].map(t => (
              <li key={t} className="flex items-center gap-2">
                <span className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0 [&_svg]:w-3.5 [&_svg]:h-3.5">{ICONS.check}</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Independent agency notice ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-4">
        <div className="flex gap-3 items-start rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-4 sm:p-5 text-sm text-slate-600 leading-relaxed">
          <svg className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" strokeWidth={1.8} viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path strokeLinecap="round" d="M12 11v5M12 8h.01" /></svg>
          <p>
            <strong className="text-slate-800">Flyairlinebooking is an independent travel agency.</strong> We are not affiliated with, endorsed by, or associated with any airline. We help customers explore available travel options and make informed booking decisions.
          </p>
        </div>
      </section>

      {/* ── Popular routes ──────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading
          eyebrow="Popular routes"
          title="Where travelers are flying"
          sub="Routes our customers often ask about. Availability, schedules and prices vary by date and airline inventory."
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ROUTES.map(r => (
            <button
              key={r.from + r.to}
              type="button"
              onClick={() => navigate(`/flights?origin=${r.from}&destination=${r.to}&date=${today}&adults=1&children=0&infants=0&passengers=1&tripType=oneway&class=economy`)}
              className="group text-left rounded-2xl ring-1 ring-slate-200 bg-white p-5 hover:ring-blue-300 hover:shadow-lg hover:-translate-y-0.5 transition-all"
            >
              <p className="text-xs font-medium text-slate-500 mb-4">{r.tag}</p>
              <div className="flex items-center gap-3">
                <div>
                  <p className="font-display text-2xl font-extrabold text-slate-900">{r.from}</p>
                  <p className="text-xs text-slate-500">{r.fromCity}</p>
                </div>
                <div className="flex-1 flex items-center gap-1.5 text-slate-300 group-hover:text-blue-500 transition-colors">
                  <span className="h-px flex-1 border-t border-dashed border-current" />
                  <svg className="w-4 h-4 rotate-90" fill="currentColor" viewBox="0 0 24 24"><path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z" /></svg>
                  <span className="h-px flex-1 border-t border-dashed border-current" />
                </div>
                <div className="text-right">
                  <p className="font-display text-2xl font-extrabold text-slate-900">{r.to}</p>
                  <p className="text-xs text-slate-500">{r.toCity}</p>
                </div>
              </div>
              <p className="mt-5 text-sm font-semibold text-blue-600 group-hover:text-blue-700">Search flights →</p>
            </button>
          ))}
        </div>
      </section>

      {/* ── Why choose us ───────────────────────────────── */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <SectionHeading eyebrow="Why Flyairlinebooking" title="Travel planning, made simpler" sub="Flight schedules, fare rules and airline policies can be confusing. We help you make sense of them." />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {WHY_CHOOSE.map(item => (
              <div key={item.title} className="bg-white rounded-2xl ring-1 ring-slate-200 p-6">
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-4">{ICONS[item.icon]}</div>
                <h3 className="font-display font-bold text-slate-900 text-lg mb-1.5">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
            <div className="rounded-2xl bg-blue-600 text-white p-6 flex flex-col justify-between">
              <div>
                <h3 className="font-display font-bold text-lg mb-1.5">Prefer to talk it through?</h3>
                <p className="text-blue-100 text-sm leading-relaxed">A travel agent can search and book with you over the phone.</p>
              </div>
              <a href={tfnHref} className="mt-5 inline-flex items-center justify-center gap-2 bg-white text-blue-700 hover:bg-blue-50 font-bold px-5 py-3 rounded-xl transition-colors">
                Call {tfn}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading eyebrow="How it works" title="Three steps to your next trip" align="center" />
        <ol className="grid md:grid-cols-3 gap-6">
          {STEPS.map(s => (
            <li key={s.n} className="relative rounded-2xl ring-1 ring-slate-200 p-6">
              <span className="w-10 h-10 rounded-full bg-slate-900 text-white font-display font-bold flex items-center justify-center mb-4">{s.n}</span>
              <h3 className="font-display font-bold text-slate-900 text-lg mb-1.5">{s.title}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* ── Support ─────────────────────────────────────── */}
      <section className="px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto rounded-3xl bg-gradient-to-br from-navy-950 to-navy-800 text-white overflow-hidden">
          <div className="grid lg:grid-cols-2 gap-10 p-6 sm:p-10 lg:p-14">
            <div>
              <p className="text-sm font-semibold text-blue-300 mb-2">Here to help</p>
              <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">Customer support you can reach</h2>
              <p className="mt-3 text-slate-300 leading-relaxed">Travel plans change. Our team assists with general booking inquiries and travel-related information, around the clock.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <a href={tfnHref} className="inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-400 text-white font-bold px-6 py-3.5 rounded-xl transition-colors">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>
                  {tfn}
                </a>
                <Link to="/contact" className="inline-flex items-center justify-center px-6 py-3.5 rounded-xl ring-1 ring-white/25 hover:bg-white/10 font-semibold transition-colors">Contact us</Link>
              </div>
              <p className="mt-6 text-xs text-slate-400 leading-relaxed max-w-md">Final decisions on schedule changes, cancellations, refunds, baggage allowances and ticket conditions are governed by the applicable airline and fare rules.</p>
            </div>
            <ul className="grid sm:grid-cols-2 gap-3 content-start">
              {SUPPORT_ITEMS.map(item => (
                <li key={item} className="flex items-center gap-3 rounded-xl bg-white/5 ring-1 ring-white/10 px-4 py-3 text-sm text-slate-200">
                  <span className="text-emerald-400 [&_svg]:w-4 [&_svg]:h-4 shrink-0">{ICONS.check}</span>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── FAQ ─────────────────────────────────────────── */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <SectionHeading eyebrow="FAQs" title="Frequently asked questions" align="center" />
        <div className="divide-y divide-slate-200 border-y border-slate-200">
          {FAQS.map((faq, i) => (
            <div key={faq.q}>
              <button
                type="button"
                aria-expanded={openFaq === i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full py-5 text-left flex justify-between items-center gap-4 font-semibold text-slate-900 hover:text-blue-700 transition-colors"
              >
                {faq.q}
                <span className={`w-7 h-7 rounded-full ring-1 ring-slate-300 flex items-center justify-center text-slate-500 shrink-0 transition-transform ${openFaq === i ? 'rotate-45' : ''}`}>+</span>
              </button>
              {openFaq === i && <p className="pb-5 -mt-1 text-slate-600 text-sm leading-relaxed pr-10">{faq.a}</p>}
            </div>
          ))}
        </div>
        <p className="text-center text-slate-500 text-sm mt-8">
          Review our{' '}
          <Link to="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</Link>,{' '}
          <Link to="/terms" className="text-blue-600 hover:underline">Terms & Conditions</Link>,{' '}
          <Link to="/refund-policy" className="text-blue-600 hover:underline">Refund Policy</Link> and{' '}
          <Link to="/service-fees" className="text-blue-600 hover:underline">Service Fees</Link>.
        </p>
      </section>
    </div>
  );
}
